/**
 * Client für Mängel an Bestandsanlagen — `/:customer/defects…` auf tri-server
 * (tri-server docs/defects.md, src/defects/defectRoutes.ts).
 *
 * Lesen und melden darf jeder Angemeldete des Mandanten; ob eine Aktion erlaubt ist, entscheidet
 * der Server (403 bzw. 409 bei einem inzwischen geänderten Status). Die Fehlermeldung des Servers
 * wird unverändert weitergereicht, damit das Panel sie zeigen kann.
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export type DefectStatus = "reported" | "open" | "resolved" | "closed" | "discarded";
export type DefectSeverity = "minor" | "major" | "critical";
export type DefectOperation = "normal" | "restricted" | "outOfService";
export type DefectAction =
  | "triage"
  | "assign"
  | "setDeadline"
  | "resolve"
  | "reopen"
  | "close"
  | "discard"
  | "comment";
export type MeasureKind = "immediate" | "repair" | "provisional" | "safeguard" | "confirmedByFollowUp";

export interface DefectMaterial {
  name: string;
  quantity: number;
  unit: string;
  billable?: boolean;
}

export interface DefectMeasure {
  kind: MeasureKind;
  description?: string | null;
  performedBy?: string | null;
  performedAt?: string | null;
  materials?: DefectMaterial[];
}

export interface DefectPhoto {
  path: string;
  kind?: "before" | "after" | "other";
  at?: string;
}

/** Ein Eintrag des Verlaufs — nur angehängt, nie umgeschrieben. Die Felder hängen am `type`. */
export interface DefectHistoryEntry {
  type: string;
  at?: string;
  by?: string | null;
  from?: any;
  to?: any;
  severity?: DefectSeverity;
  assignee?: string | null;
  reason?: string;
  comment?: string;
  kind?: MeasureKind;
  status?: DefectStatus;
}

export interface Defect {
  _id: string;
  /** Lesbare Nummer, `V-<Jahr>-<6 Zeichen der Id>`. */
  mark?: string;
  asset: string;
  title: string;
  description?: string | null;
  source?: "adHoc" | "checklist" | "document" | "acceptance";
  kind?: "damage" | "deficiency";
  reportedSeverity?: "danger" | "notWorking" | "cosmetic" | null;
  severity: DefectSeverity;
  operation?: DefectOperation;
  status: DefectStatus;
  openReason?: string | null;
  openReasonNote?: string | null;
  deadline?: string | null;
  deadlineOverrideReason?: string | null;
  assignee?: string | null;
  registered?: string;
  registeredBy?: string | null;
  remediedAt?: string | null;
  closedAt?: string | null;
  photos?: DefectPhoto[];
  measures?: DefectMeasure[];
  /** Nur am Einzelabruf; die Liste lässt den Verlauf weg. */
  history?: DefectHistoryEntry[];
  /** Mängelanspruch (tri-server docs/claims.md): gesetzt, wenn die Gewährleistungsweiche einen Vertrag fand. */
  claimStatus?: string | null;
  contract?: string | null;
  /** Nur in der Liste: vom Server berechnet. */
  overdue?: boolean;
  /** Nur in der Liste: die Anlage in Kurzform. */
  assetInfo?: { name?: string | null; id?: string | null; schemaCategory?: string | null; building?: string | null } | null;
}

export interface DefectList {
  defects: Defect[];
  /** Der Server liefert höchstens 500; `true` heißt, es gibt mehr. */
  truncated: boolean;
}

const processResponse = async (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }

  throw new ApplicationError(errorMessage);
};

const JSON_HEADERS = { "Content-Type": "application/json" };

export const useDefectsAPI = (customer: string) => {
  const baseUrl = `${configuration.serverUrl}/${customer}/defects`;

  return {
    /** `query` ist die fertige Abfrage ohne `?` (siehe `defectQuery`). */
    listDefects: async (query: string): Promise<DefectList> => {
      const response = await authenticatedFetch(`${baseUrl}${query ? `?${query}` : ""}`, {
        method: "GET",
        headers: JSON_HEADERS,
      });
      const result = await processResponse(response);

      return {
        defects: Array.isArray(result?.defects) ? result.defects : [],
        truncated: result?.truncated === true,
      };
    },

    getDefect: async (id: string): Promise<Defect> => {
      const response = await authenticatedFetch(`${baseUrl}/${encodeURIComponent(id)}`, {
        method: "GET",
        headers: JSON_HEADERS,
      });

      return processResponse(response);
    },

    /** Anlegen; die Antwort ist der neue Mangel (201) samt `mark`. */
    createDefect: async (body: Record<string, unknown>): Promise<Defect> => {
      const response = await authenticatedFetch(baseUrl, { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) });

      return processResponse(response);
    },

    act: async (id: string, action: DefectAction, body: Record<string, unknown>): Promise<Defect> => {
      const response = await authenticatedFetch(
        `${baseUrl}/${encodeURIComponent(id)}/actions/${action}`,
        { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) },
      );

      return processResponse(response);
    },

    /**
     * Export als Datei. Der Server setzt den Dateinamen; ein `<a href>` ginge ohne den
     * Authorization-Header, deshalb über `fetch` und einen Blob.
     */
    downloadCsv: async (query: string, format: "csv" | "materials"): Promise<void> => {
      const params = new URLSearchParams(query);
      params.set("format", format);
      const response = await authenticatedFetch(`${baseUrl}?${params.toString()}`, { method: "GET" });

      if (!response.ok) {
        await processResponse(response);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = format === "materials" ? "material.csv" : "maengel.csv";
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    },
  };
};
